import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, PLAN_LABELS } from "../lib/config";
import { Loader2, Sparkles, Users, Target, Heart } from "lucide-react";

export default function FamiliaSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/public/progress");
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // refresh every 60s (was 15s) to reduce load
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <section className="bg-black py-24 text-center">
        <Loader2 className="w-8 h-8 text-yellow-400 animate-spin mx-auto" />
      </section>
    );
  }

  const pct = data?.global_percentage || 0;
  const phrases = data?.milestone_phrases || [];
  const total = phrases.length;
  // active milestone index (the highest that's been reached)
  const reachedIdx = Math.max(0, Math.floor((pct / 100) * total) - (pct >= 100 ? 0 : 1));

  return (
    <section className="relative bg-gradient-to-br from-black via-zinc-900 to-black py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
      <div className="absolute inset-0 grain opacity-30" />
      {/* Decorative glow */}
      <div className="absolute -top-32 left-1/4 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 border border-yellow-400/40 bg-yellow-400/10 text-yellow-400 text-xs uppercase tracking-[0.4em] font-bold mb-6">
            <Sparkles className="w-3 h-3 inline mr-2" />
            Familia Operación Rescate
          </span>
          <h2 className="font-heading uppercase text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
            Cada cuota es un<br />
            <span className="text-gradient-gold">paso al rescate</span>
          </h2>
          <p className="mt-6 text-white/70 text-base lg:text-lg max-w-2xl mx-auto">
            Mira en tiempo real cómo crece nuestra familia y cómo nos acercamos juntos al campamento.
          </p>
        </motion.div>

        {/* Global stats row */}
        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          <StatTile icon={Users} value={data?.total_registered || 0} label="Inscritos" />
          <StatTile icon={Target} value={`$${data?.total_collected || 0}`} label="Recaudado" sub={`de $${data?.total_goal || 0}`} />
          <StatTile icon={Heart} value={`${pct}%`} label="Al rescate" highlighted />
        </div>

        {/* Milestone progress bar */}
        <div className="mb-16" data-testid="milestone-bar">
          <div className="relative h-3 bg-white/10 rounded-full overflow-hidden mb-6">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500"
            />
            {/* Milestone dots */}
            {phrases.map((_, i) => {
              const left = ((i + 1) / total) * 100;
              const reached = i <= reachedIdx && pct >= ((i + 1) / total) * 100 * 0.5;
              return (
                <div
                  key={i}
                  className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 transition-all ${
                    reached
                      ? "bg-yellow-400 border-yellow-300 shadow-lg shadow-yellow-400/50 scale-125"
                      : "bg-zinc-900 border-white/30"
                  }`}
                  style={{ left: `${left}%`, transform: `translate(-50%, -50%)` }}
                />
              );
            })}
          </div>

          {/* Active milestone phrase */}
          <motion.div
            key={reachedIdx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center min-h-[60px] flex items-center justify-center"
            data-testid="milestone-phrase"
          >
            <div className="font-heading uppercase text-yellow-400 text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              "{phrases[Math.min(reachedIdx, total - 1)]}"
            </div>
          </motion.div>

          {/* Tiny phrases timeline */}
          <div className="hidden md:grid grid-cols-13 mt-4 text-[10px] text-white/40 uppercase tracking-widest" style={{ gridTemplateColumns: `repeat(${total}, 1fr)` }}>
            {phrases.map((p, i) => (
              <div
                key={i}
                className={`px-1 text-center truncate ${i <= reachedIdx ? "text-yellow-400/80" : ""}`}
                title={p}
              >
                S{i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Registrant list */}
        <div>
          <h3 className="font-heading uppercase text-white text-3xl mb-6 text-center">Familia inscrita</h3>
          {data?.registrants?.length === 0 ? (
            <div className="text-center py-12 text-white/40">
              <Heart className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p className="text-lg">¡Sé el primero en unirte!</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[480px] overflow-y-auto pr-2" data-testid="registrants-list">
              {data?.registrants?.map((r, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.03 }}
                  className="p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl hover:border-yellow-400/40 transition-colors"
                  data-testid={`registrant-${i}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {r.photo ? (
                        <img
                          src={r.photo}
                          alt={r.display_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-yellow-400/60"
                          data-testid={`registrant-avatar-${i}`}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-600 flex items-center justify-center text-black font-heading text-lg border-2 border-yellow-400/60">
                          {(r.display_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      {r.percentage_paid >= 100 && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-400 border-2 border-zinc-900 flex items-center justify-center">
                          <span className="text-[8px] text-black font-bold">✓</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm truncate">{r.display_name}</div>
                      <div className="text-white/40 text-[10px] uppercase tracking-widest truncate">
                        {r.age_category === "adult" ? "Adulto" : "Niño"} · {PLAN_LABELS[r.plan_type]}
                      </div>
                    </div>
                    <div className={`font-heading text-2xl flex-shrink-0 ${r.percentage_paid >= 100 ? "text-green-400" : "text-yellow-400"}`}>
                      {r.percentage_paid}%
                    </div>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${r.percentage_paid}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1 }}
                      className={`h-full ${r.percentage_paid >= 100 ? "bg-green-400" : "bg-gradient-to-r from-yellow-400 to-orange-500"}`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          {/* CTA: Ver todos */}
          {data?.registrants?.length > 0 && (
            <div className="mt-10 text-center">
              <a
                href="/inscritos"
                data-testid="see-all-participants"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full border-2 border-yellow-400/60 text-yellow-400 hover:bg-yellow-400 hover:text-black font-bold uppercase tracking-widest text-sm transition-colors"
              >
                Ver los {data.total_registered} inscritos →
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StatTile({ icon: Icon, value, label, sub, highlighted }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className={`p-6 rounded-2xl text-center border ${
        highlighted
          ? "bg-gradient-to-br from-yellow-400/10 to-orange-500/10 border-yellow-400/40"
          : "bg-white/5 border-white/10"
      }`}
    >
      <Icon className={`w-6 h-6 mx-auto mb-3 ${highlighted ? "text-yellow-400" : "text-white/60"}`} />
      <div className={`font-heading text-4xl lg:text-5xl ${highlighted ? "text-gradient-gold" : "text-white"}`}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{label}</div>
      {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
    </motion.div>
  );
}
